import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Divider,
  Tooltip,
  IconButton,
  alpha,
  InputAdornment,
} from "@mui/material";
import { useSelector } from "react-redux";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { ContainedButton } from "../../Common/Buttons";

const PromotionsPage = () => {
  const { userInfo, btncolor } = useSelector((state) => state);
  const [email, setEmail] = useState("");
  const [referredList, setReferredList] = useState([]);
  const [showReferredUsers, setShowReferredUsers] = useState(false);
  const [currency, setCurrency] = useState("");
  const [emailError, setEmailError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getCurrency();
    fetchReferredUsers();
  }, [userInfo]);

  const getCurrency = async () => {
    try {
      const response = await axios.post(
        `${WEBSERVICE}user/exchangeReferralDetails`,
        { uuid: localStorage.getItem("uuid") },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setCurrency(response.data.currency);
    } catch {
      showToast("error", "Internal Server Error");
    }
  };

  const fetchReferredUsers = async () => {
    const referralCode = userInfo?.userResult?.referralCode;
    if (!referralCode) return;
    try {
      const response = await axios.post(
        `${WEBSERVICE}user/allUsersByReferredUser`,
        { referredBy: referralCode },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.error.error_data !== 0) {
        showToast("error", response.data.error.error_msg);
      } else {
        setReferredList(response.data.users);
        setShowReferredUsers(response.data.users.length !== 0);
      }
    } catch {
      showToast("error", "Internal Server Error");
    }
  };

  const handleReferralEmail = async () => {
    try {
      const response = await axios.post(
        `${WEBSERVICE}user/referFriend`,
        {
          email,
          uuid: localStorage.getItem("uuid"),
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response.data.error.error_data !== 0) {
        showToast("error", response.data.error.error_msg);
      } else {
        showToast("success", "Successfully Sent");
        setEmail("");
        fetchReferredUsers();
      }
    } catch {
      showToast("error", "Internal Server Error");
    }
  };

  const validateEmail = (value) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) return "Email is required";
    if (!regex.test(value)) return "Invalid email format";
    return "";
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handleCopyCode = () => {
    const code = userInfo?.userResult?.referralCode;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const referralCode = userInfo?.userResult?.referralCode || "";
  const shareUrl = `${window.location.origin}/signup?ref=${referralCode}`;

  const socialLinks = [
    {
      icon: <WhatsAppIcon sx={{ fontSize: 20 }} />,
      label: "WhatsApp",
      color: "#25D366",
      href: `https://wa.me/?text=Join%20me%20on%20this%20platform%20using%20my%20referral%20code%3A%20${referralCode}`,
    },
    {
      icon: <FacebookIcon sx={{ fontSize: 20 }} />,
      label: "Facebook",
      color: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      icon: <TwitterIcon sx={{ fontSize: 20 }} />,
      label: "X (Twitter)",
      color: "#1DA1F2",
      href: `https://twitter.com/intent/tweet?text=Join%20me%20using%20my%20referral%20code%3A%20${referralCode}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      icon: <LinkedInIcon sx={{ fontSize: 20 }} />,
      label: "LinkedIn",
      color: "#0A66C2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
  ];

  return (
    <Box sx={pageWrapper}>

      {/* ── Hero Banner ── */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <PeopleAltOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography sx={heroTitle}>Refer a Friend & Earn</Typography>
            <Typography sx={heroSubtitle}>
              Share your unique referral code and earn {currency || "crypto"} rewards when your friends sign up and complete their first transaction
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Main Content Grid ── */}
      <Box sx={contentGrid}>

        {/* ── Left Column: Code + Social Share ── */}
        <Box sx={leftColumn}>

          {/* Referral Code Card */}
          <Box sx={mainCard}>
            <Box sx={sectionHeader}>
              <CardGiftcardOutlinedIcon sx={{ fontSize: 18, color: btncolor }} />
              <Typography sx={sectionHeaderText}>Your Referral Code</Typography>
            </Box>
            <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 2.5 }} />

            <Box sx={codeBlock}>
              <Typography sx={codeValue}>
                {referralCode || "—"}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy code"} arrow>
                <IconButton
                  size="small"
                  onClick={handleCopyCode}
                  sx={{
                    color: copied ? "#16a34a" : btncolor,
                    backgroundColor: copied
                      ? "rgba(22,163,74,0.08)"
                      : alpha(btncolor || "#1976d2", 0.08),
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: copied
                        ? "rgba(22,163,74,0.15)"
                        : alpha(btncolor || "#1976d2", 0.15),
                    },
                  }}
                >
                  {copied
                    ? <CheckOutlinedIcon sx={{ fontSize: 18 }} />
                    : <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            </Box>

            <Typography sx={codeCaption}>
              Share this code with your friends. You'll earn free {currency || "crypto"} when they complete KYC and their first transaction.
            </Typography>
          </Box>

          {/* Social Share Card */}
          <Box sx={mainCard}>
            <Box sx={sectionHeader}>
              <SendOutlinedIcon sx={{ fontSize: 18, color: btncolor }} />
              <Typography sx={sectionHeaderText}>Share via Social</Typography>
            </Box>
            <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 2.5 }} />

            <Box sx={socialGrid}>
              {socialLinks.map((s, i) => (
                <Tooltip key={i} title={`Share on ${s.label}`} arrow>
                  <Box
                    component="a"
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      ...socialBtn,
                      "&:hover": {
                        backgroundColor: alpha(s.color, 0.12),
                        borderColor: alpha(s.color, 0.45),
                        color: s.color,
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 12px ${alpha(s.color, 0.2)}`,
                      },
                    }}
                  >
                    <Box sx={{ color: s.color, display: "flex" }}>{s.icon}</Box>
                    <Typography sx={socialBtnText}>{s.label}</Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>

        </Box>

        {/* ── Right Column: Email Invite ── */}
        <Box sx={rightColumn}>
          <Box sx={{ ...mainCard, height: "100%" }}>
            <Box sx={sectionHeader}>
              <PersonAddOutlinedIcon sx={{ fontSize: 18, color: btncolor }} />
              <Typography sx={sectionHeaderText}>Invite via Email</Typography>
            </Box>
            <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 2.5 }} />

            <Typography sx={inviteDesc}>
              Enter your friend's email address and we'll send them an invitation with your referral code.
            </Typography>

            <Box sx={fieldWrap}>
              <Typography sx={fieldLabel}>
                <EmailOutlinedIcon sx={fieldLabelIcon} /> Friend's Email Address
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter email address"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ fontSize: 18, color: "var(--text-color)", opacity: 0.4 }} />
                    </InputAdornment>
                  ),
                }}
                sx={modernFieldStyle}
              />
            </Box>

            <ContainedButton
              variant="contained"
              fullWidth
              onClick={handleReferralEmail}
              disabled={!!emailError || !email}
              sx={submitBtn}
            >
              Send Invitation
            </ContainedButton>

            {/* Reward Note */}
            <Box sx={rewardNote}>
              <Box sx={{ ...rewardDot, backgroundColor: alpha(btncolor || "#1976d2", 0.15), color: btncolor }}>
                <CardGiftcardOutlinedIcon sx={{ fontSize: 14 }} />
              </Box>
              <Typography sx={rewardNoteText}>
                The referral bonus is credited to your <strong>{currency || "crypto"}</strong> wallet once the referred person completes KYC and executes their first transaction.
              </Typography>
            </Box>
          </Box>
        </Box>

      </Box>

      {/* ── Referred Users Table ── */}
      {showReferredUsers && (
        <Box sx={tableSection}>
          <Box sx={tableSectionHeader}>
            <Box>
              <Typography sx={tableSectionTitle}>Referred Users</Typography>
              <Typography sx={tableSectionCaption}>
                {referredList.length} user{referredList.length !== 1 ? "s" : ""} referred so far
              </Typography>
            </Box>
          </Box>

          <TableContainer component={Paper} sx={modernTableContainer}>
            <Table sx={tableStyle}>
              <TableHead>
                <TableRow sx={modernTableHeadRow}>
                  {["#", "Name", "Email"].map((h, i) => (
                    <TableCell key={i} sx={modernTableHeadCell}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {referredList.length > 0 ? (
                  referredList.map((m, index) => (
                    <TableRow key={index} sx={modernTableBodyRow}>
                      <TableCell sx={{ ...modernTableBodyCell, width: 48, color: "var(--text-color)", opacity: 0.4 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={modernTableBodyCell}>{m.firstName}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{m.email}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} sx={emptyCell}>
                      <InboxOutlinedIcon sx={emptyIcon} />
                      <Typography sx={emptyText}>No referred users yet</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

    </Box>
  );
};

// ── Styles (mirrors Report.js / RecurringPayment.js conventions) ──────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

// ── Content Layout ─────────────────────────────────────────────────────────────

const contentGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
  gap: 2.5,
  mb: 3,
  alignItems: "start",
};

const leftColumn = {
  display: "flex",
  flexDirection: "column",
  gap: 2.5,
};

const rightColumn = {
  display: "flex",
  flexDirection: "column",
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
  p: { xs: 2.5, md: 3 },
};

const sectionHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 2,
};

const sectionHeaderText = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--text-color)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

// ── Referral Code Block ────────────────────────────────────────────────────────

const codeBlock = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "var(--body-color-less2)",
  borderRadius: "12px",
  px: 2.5,
  py: 1.75,
  mb: 2,
  border: "1px solid rgba(125,125,125,0.15)",
};

const codeValue = {
  fontSize: "1.6rem",
  fontWeight: 800,
  color: "var(--text-color)",
  letterSpacing: "0.12em",
  lineHeight: 1,
};

const codeCaption = {
  fontSize: "0.8rem",
  color: "var(--text-color)",
  opacity: 0.55,
  lineHeight: 1.6,
};

// ── Social Share ──────────────────────────────────────────────────────────────

const socialGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 1.25,
};

const socialBtn = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.75,
  py: 1.1,
  borderRadius: "10px",
  border: "1px solid rgba(125,125,125,0.2)",
  backgroundColor: "var(--body-color-less2)",
  cursor: "pointer",
  textDecoration: "none",
  transition: "all 0.2s ease",
  color: "var(--text-color)",
};

const socialBtnText = {
  fontSize: "0.82rem",
  fontWeight: 600,
  color: "var(--text-color)",
  lineHeight: 1,
};

// ── Email Invite ──────────────────────────────────────────────────────────────

const inviteDesc = {
  fontSize: "0.85rem",
  color: "var(--text-color)",
  opacity: 0.65,
  lineHeight: 1.65,
  mb: 2.5,
};

const fieldWrap = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  mb: 2,
};

const fieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: "var(--text-color)",
  opacity: 0.55,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

const fieldLabelIcon = {
  fontSize: 14,
  color: "var(--text-color)",
  opacity: 0.6,
};

const modernFieldStyle = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.7 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
  "& .MuiFormHelperText-root": {
    fontSize: "0.72rem",
    color: "#ef4444",
    mx: 0,
  },
};

const submitBtn = {
  height: "48px",
  borderRadius: "10px",
  fontWeight: 600,
  fontSize: "0.88rem",
  textTransform: "none",
  letterSpacing: "0.01em",
  mb: 2.5,
};

const rewardNote = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.25,
  p: 1.75,
  borderRadius: "10px",
  backgroundColor: "var(--body-color-less2)",
  border: "1px solid rgba(125,125,125,0.13)",
};

const rewardDot = {
  width: 28,
  height: 28,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const rewardNoteText = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.65,
  lineHeight: 1.65,
};

// ── Referred Users Table ──────────────────────────────────────────────────────

const tableSection = {
  mt: 1,
};

const tableSectionHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1.5,
  mb: 2,
};

const tableSectionTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const tableSectionCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.25,
};

const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflow: "hidden",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 420,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

export default PromotionsPage;
